import type { ChoreoLink } from './calendar'

/**
 * One class in the list on the classes screen (D-205).
 *
 * Carries everything the screen groups or filters by, because the grouping
 * happens in the browser — 185 classes is a smaller payload than one of the
 * photographs on the same page, and a "Group by" control that waits for the
 * server does not feel like a control.
 */
export type ClassRow = {
  classId: string
  name: string
  routineName: string | null
  hasMusic: boolean
  dayOfWeek: string
  /** Sunday-first, so a list orders by the week rather than the alphabet. */
  dayNumber: number
  startTime: string | null
  roomName: string | null
  programName: string
  /** Competitive is gated and never marketed; recreational is the reverse (D-205). */
  isCompetitive: boolean
  classTypeId: string | null
  /** Every counts-as-one set it is in; a class may be in several (D-657). */
  attendanceGroupIds: string[]
  ageLabel: string | null
  showInPublicCatalog: boolean
  enrolled: number
  maxSize: number | null
  /** False for the 86 solos with no slot yet — they meet on no dates (D-84). */
  isFullyScheduled: boolean
  /**
   * It alternates and nobody has confirmed which week (D-603).
   *
   * The import read `BIWKLY` in the name and cannot know which fortnight, so
   * each of these is a teacher somebody has to ask. On the row because the job
   * is finding them, not reading one.
   */
  meetingWeekUnconfirmed: boolean

  /** Who teaches it, by name, so the list can be filtered by teacher (D-762). */
  instructors: string[]
}

/**
 * A session on a date its class no longer meets (D-687).
 *
 * <b>Two different problems in one list, told apart by `happened`.</b> A past
 * one is history — the class met that evening and the marks count — so there is
 * nothing to do but explain why it is on the calendar. A future one is an
 * arrangement for an evening that will not happen, and is the one worth acting
 * on.
 */
export type OffPatternSession = {
  occurrenceId: string
  date: string
  happened: boolean
  isCancelled: boolean
  marked: number
}

/** One dated session, with what it offers to repair (D-281). */
export type ClassSession = {
  occurrenceId: string
  date: string
  startTime: string | null
  isCancelled: boolean
  repairs: "Tech" | "Choreo" | "Recital" | null
  style: string | null
  levelBand: string | null
  minimumSignups: number | null
  signups: number

  /**
   * One dancer's private repair, which carries a designation of its own (D-406).
   *
   * Read by *run this one again*, which must never copy it: it is a lesson made
   * for one absence, not a session anybody else could sign up to (D-837).
   */
  isPrivateRepair: boolean
}

export type ClassAdminView = {
  classId: string
  name: string
  routineName: string | null
  musicUrl: string | null
  /** The practice videos for families (D-541), in order; empty when none. */
  choreoLinks?: ChoreoLink[]
  description: string | null
  style: string | null
  dayOfWeek: string
  startTime: string | null
  endTime: string | null
  roomId: string | null
  roomName: string | null
  /** True for a class the studio made — its day, time and room are editable (D-207). */
  isOurs: boolean
  programName: string
  /**
   * Its programme's id, so this class can be copied (D-837).
   *
   * The screen had the name and nothing it could send back, and matching a name to
   * an id in the browser is how a copy lands in the wrong programme — which is what
   * attendance, capacity and self-registration all read (D-191).
   */
  studioProgramId: string
  ageLabel: string | null
  minAgeYears: number | null
  maxAgeYears: number | null
  /** True once the studio set the range itself, so the next import leaves it (D-206). */
  ageRangeIsOurs: boolean
  /** What the class is for makeup purposes, and the levels it covers (D-280). */
  makeupCategory: "Tech" | "Choreo" | "Recital" | null
  /** As the studio writes it - "2/3", or empty for a class with no level. */
  levelBand: string | null
  makeupCategoryIsOurs: boolean
  /**
   * The name says technique and the category says otherwise (D-415).
   *
   * Reported, never corrected — only the studio knows which of the two is
   * wrong (D-51, D-227). Worth saying out loud because D-405 made it
   * consequential: a class filed as choreo sends the family to a paid private
   * rather than a free scheduled makeup, and nothing on any screen said why.
   */
  nameDisagreesWithCategory: boolean
  /**
   * This class can never offer its dancers a makeup (D-495).
   *
   * An empty style is an absolute refusal in the matcher and it looks like
   * nothing on every screen — 153 of 186 real classes have no style, 28 of
   * them on the group path. Answered by the server because *which* categories
   * repair by group session is a domain rule (D-405).
   */
  offersNoMakeups?: boolean
  /**
   * The studio does not ask anybody to make this one up (D-658).
   *
   * Separate from the category, which says what the class *is*. This says what
   * the studio asks of it, so an import can never overwrite it.
   */
  noMakeup: boolean
  /** A rehearsal class, where a sub-team can be declared not called (D-842). */
  takesACall: boolean
  /** Every week, or every other week (D-603). */
  cadence: 'Weekly' | 'Fortnightly'
  /** True once the studio answered it, so the next import leaves it (D-206). */
  cadenceIsOurs: boolean
  /** The evening the alternation counts from — `yyyy-MM-dd`. */
  firstMeeting: string
  /** It alternates on the import's word alone, and nobody has said which week. */
  meetingWeekUnconfirmed: boolean
  /**
   * It runs on a single date, so it is never asked how often it meets.
   *
   * A monthly makeup and a one-off workshop are one evening (D-402), and the
   * pattern control would stretch either into a series. Answered by the
   * server, which refuses the edit for the same reason.
   */
  meetsOnOneDate: boolean
  levelBandIsOurs: boolean
  styleIsOurs: boolean
  /** This class dated sessions from today on, so one can be offered as a makeup (D-281). */
  sessions: ClassSession[]

  /**
   * Sessions sitting on dates this class no longer meets (D-687).
   *
   * <b>Why a class just made fortnightly can still look weekly.</b> Changing
   * the pattern re-syncs the dates and sweeps the ones that dropped off it —
   * except any carrying something that would be lost, which are kept on
   * purpose. Until D-687 nothing said so.
   *
   * <b>`happened` splits the list into two different problems.</b> A past one
   * with a register is not a leftover: the class met, the dancers were marked,
   * and it counts towards their percentages. A future one survived because
   * somebody arranged cover for an evening that will now not happen.
   */
  offPattern: OffPatternSession[]
  maxSize: number | null
  enrolled: number
  showInPublicCatalog: boolean
  registrationId: string | null
  instructors: string[]

  /** The same people by id, so the panel can edit the set (D-537). */
  instructorStaffIds: string[]

  /** Of those, the ones assisting rather than teaching (D-538). */
  studentTeacherStaffIds: string[]
  /** Fields the next Jackrabbit import rewrites (D-124). */
  ownedByJackrabbit: string[]
  /**
   * What is keeping this class off the public page (D-261). Empty means it is
   * on it.
   *
   * <b>Answered by the server, which owns the rule.</b> The panel used to
   * promise "Families can see this class and register for it" next to the
   * tickbox with no idea whether that was true — a competitive class, or one
   * with no room, was dropped in silence and the sentence stayed put.
   */
  notOnSiteBecause: CatalogBlocker[]
  /** The kind's name, so a shut gate can say which kind to go and switch on. */
  kindName: string | null
  /**
   * Which kind this class is filed under (D-390).
   *
   * The panel used to take this from the list row that opened it, and the list
   * groups by kind - so filing a class under a new kind regrouped the list,
   * moved the row, and the panel showed another group value. It comes from the
   * class itself now.
   */
  classTypeId: string | null
}

/**
 * A reason a class is not on the public page (D-261).
 *
 * Codes rather than sentences, so the wording stays in the component and the
 * rule stays on the server. `KindSwitchedOff` and `ProgrammeNotPublic` are
 * mutually exclusive: the kind answers when there is one, the programme when
 * there is not.
 */
export type CatalogBlocker =
  | 'ClassSwitchedOff'
  | 'KindSwitchedOff'
  | 'ProgrammeNotPublic'
  | 'NotFullyScheduled'

export async function fetchClasses(q = '', missingMusic = false): Promise<ClassRow[]> {
  const params = new URLSearchParams()
  if (q.trim()) params.set('q', q.trim())
  if (missingMusic) params.set('missingMusic', 'true')

  const res = await fetch(`/api/admin/classes?${params}`)
  if (!res.ok) throw new Error(`Could not load the classes (${res.status})`)

  return res.json()
}

export async function fetchClass(id: string): Promise<ClassAdminView> {
  const res = await fetch(`/api/admin/classes/${id}`)
  if (!res.ok) throw new Error(`Could not load that class (${res.status})`)

  return res.json()
}

export async function saveClass(
  id: string,
  edit: {
    /**
     * A new name, for a class the studio made (D-633). Omitted leaves it
     * alone; the server refuses it on an imported class, whose name is
     * Jackrabbit's and is what the enrolment stage matches on.
     */
    name?: string
    routineName: string | null
    musicUrl: string | null
    /** The practice videos, whole list (D-541); blank addresses are dropped by the server. */
    choreoLinks?: ChoreoLink[]
    description: string | null
    style: string | null
    /** Omit to leave the current setting alone. */
    showInPublicCatalog?: boolean
    /**
     * Distinguishes "leave the ages alone" from "make it all ages" (D-206).
     * Both send nulls for the bounds; only this says which was meant.
     */
    setAges?: boolean
    minAgeYears?: number | null
    maxAgeYears?: number | null
    /**
     * Distinguishes "leave the makeup fields alone" from "clear them" (D-280).
     * Governs the style too, which is why the panel may send one at all — sent
     * unconditionally it destroyed a seeded style on a routine edit.
     */
    setMakeupFields?: boolean
    makeupCategory?: string | null
    /** As the studio writes it — "2/3", or empty for a class with no level. */
    levelBand?: string | null
    /**
     * Whether the studio asks anybody to make this class up (D-658).
     *
     * Outside `setMakeupFields`: Jackrabbit has no such field, so there is no
     * seeded value to protect. Omit to leave it alone.
     */
    noMakeup?: boolean
    takesACall?: boolean
    /** Refused on an imported class, where the next import owns these (D-207). */
    setSchedule?: boolean
    dayOfWeek?: string
    startTime?: string | null
    endTime?: string | null
    roomId?: string | null
    /**
     * How often it meets, and the evening that counts from (D-603).
     *
     * Deliberately not part of `setSchedule`, which the server refuses on an
     * imported class — every import rewrites the day and the time (D-124) and
     * none of them touches the dates, so these two are the studio's to answer
     * on all twelve of the real ones.
     */
    setMeeting?: boolean
    cadence?: 'Weekly' | 'Fortnightly'
    firstMeeting?: string
  },
): Promise<ClassAdminView> {
  const res = await fetch(`/api/admin/classes/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(edit),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? `Could not save that (${res.status})`)
  }

  return res.json()
}

/**
 * Offer a session as a makeup, or withdraw it (D-281).
 *
 * A null `repairs` withdraws the offer and leaves the session running -
 * withdrawing is not cancelling, and cancelling takes a session out of every
 * dancer denominator (A-2).
 */
export async function designateMakeup(
  classId: string,
  occurrenceIds: string[],
  edit: {
    repairs: string | null
    style: string | null
    levelBand: string | null
    minimumSignups: number | null
  },
): Promise<{ reaches: number | null }> {
  /*
   * **Several dates in one save** (D-411). `asked`, on a screenshot of the
   * single-date control: *"She should be able to select mulitple dates, as
   * they offer makeup classes once a month."*
   *
   * All or nothing on the server: every session is checked before any is
   * written, so a tick list containing one cancelled date does not leave half
   * of them offered.
   */
  const res = await fetch(`/api/admin/classes/${classId}/occurrences/makeup`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ occurrenceIds, designation: edit }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.error ?? `Could not save that (${res.status})`)
  }

  /*
   * **How many classes it can repair an absence from** (D-514).
   *
   * `asked`, beside the report that became D-510: *she offered a session here
   * and the parent portal then found nothing*. A session reaches a family only
   * when its kind, its style and its level all line up with the class they
   * missed, and nothing studio-side asked that — so a typo in the style saved
   * happily and was offered to nobody.
   *
   * `null` means she was withdrawing an offer, which reaches nothing by
   * definition and is never a mistake.
   *
   * Read back rather than dropped: a server that says something and a client
   * that throws it away is the fault D-376 records twice on this project.
   */
  const body = await res.json().catch(() => null)
  return { reaches: typeof body?.reaches === 'number' ? body.reaches : null }
}

export type NewClassOptions = {
  programs: { id: string; name: string; isCompetitive: boolean }[]
  rooms: { id: string; name: string }[]
  /** Stated, not asked — a hand-made class runs across the season (D-42). */
  seasonName: string | null
  seasonStart: string | null
  seasonEnd: string | null
  /** Who could teach it (D-404). */
  teachers: { id: string; name: string }[]

  /*
   * ⚠ There used to be a `recent` list of make-up sessions here, to start the
   * next one from (D-410). It is gone (D-837) — the copy is offered on the
   * session's own class record now, as *run this one again*, which is where the
   * owner looked for it.
   */
}

export async function fetchNewClassOptions(): Promise<NewClassOptions> {
  const res = await fetch('/api/admin/classes/options')
  if (!res.ok) throw new Error(`Could not load that (${res.status})`)
  return res.json()
}

export type NewClass = {
  name: string
  studioProgramId: string
  dayOfWeek: string
  startTime: string | null
  endTime: string | null
  roomId: string | null
  classTypeId: string | null
  /**
   * What a missed session of this class may be repaired by (scenario 9, D-295).
   *
   * All three may be left empty, and empty stays empty — defaulting one would
   * be the app deciding a studio policy nobody stated.
   */
  makeupCategory: 'Tech' | 'Choreo' | 'Recital' | null
  style: string | null
  levelBand: string | null
  /**
   * Whether Jackrabbit holds this class too (D-207).
   *
   * Not stored — it decides how hard the name is checked. Yes means the next
   * import must recognise it, so a near-match to an existing class is refused
   * rather than allowed to split a roster.
   */
  alsoInJackrabbit: boolean
  /**
   * The one date this class meets, when it meets once (D-402).
   *
   * Omitted, it runs the season as every hand-made class always has (D-207).
   * Sent, the class starts and ends on that day, which is the shape
   * `ScheduledDates` already documents for a single Saturday workshop (D-58)
   * — so a monthly makeup needs no recurrence primitive, just one a month.
   *
   * The weekday follows the date on the server; sending a Saturday with
   * `dayOfWeek: 'Monday'` would otherwise generate nothing at all, silently.
   */
  runsOn?: string | null
  /**
   * Designate the session this creates as a makeup, in the same call.
   *
   * Explicit rather than inferred: a one-date class carrying a makeup category
   * could as easily be a Saturday workshop. What it buys is the end of the
   * two-form dance — make the class here, then find its occurrence on another
   * screen and designate it there.
   */
  isMakeupSession?: boolean
  /** Empty means the studio default (A-16). */
  makeupMinimumSignups?: number | null
  /**
   * Who teaches it (D-404).
   *
   * Until this existed nothing in production assigned an instructor, so every
   * class the app made had none - invisible until a level-up makeup needed one
   * to approve it and the request reached nobody.
   */
  teacherStaffId?: string | null
}

export async function createClass(input: NewClass): Promise<ClassAdminView> {
  const res = await fetch('/api/admin/classes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? `Could not add that class (${res.status})`)
  }

  return res.json()
}

/**
 * A batch of classes on or off the website (D-205).
 *
 * Its own endpoint rather than a loop over {@link saveClass}, which writes every
 * field it is given — a bulk hide routed through it would have to send each
 * class's routine name and music link back correctly or silently erase them.
 */
export async function setClassVisibility(
  classIds: string[],
  showInPublicCatalog: boolean,
): Promise<void> {
  const res = await fetch('/api/admin/classes/visibility', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ classIds, showInPublicCatalog }),
  })

  if (!res.ok) throw new Error(`Could not change those (${res.status})`)
}

/**
 * Whether a pasted value could plausibly be a folder link.
 *
 * Mirrors the server, and is deliberately shallow: a Dropbox share URL is long,
 * signed and full of characters a stricter check would reject. The failure
 * worth catching is somebody pasting a folder *name*.
 */
export function musicUrlProblem(value: string): string | null {
  const trimmed = value.trim()
  if (trimmed.length === 0) return null

  return /^https?:\/\/\S+$/i.test(trimmed)
    ? null
    : 'Paste the whole address, starting with https://'
}

/**
 * Why a class ticked for the website still is not on it (D-261).
 *
 * <b>The class's own switch is deliberately left out.</b> Somebody who has just
 * unticked the box does not need to be told the box is unticked; the message
 * worth showing is the one they cannot see from the form — that a shut kind or
 * a missing room is what is actually in the way.
 *
 * Empty means nothing else is stopping it, so the tick does what it says.
 */
export function whyStillNotOnSite(
  blockers: CatalogBlocker[],
  kindName: string | null,
): string[] {
  return blockers
    .filter((b) => b !== 'ClassSwitchedOff')
    .map((b) => {
      switch (b) {
        case 'KindSwitchedOff':
          return kindName
            ? `${kindName} is switched off for the website, so this class stays off it.`
            : 'Its kind of class is switched off for the website, so this class stays off it.'

        case 'ProgrammeNotPublic':
          // The competitive default, and the reason that applies to 157 of the
          // studio's 185 classes — every one of them is filed under no kind.
          return (
            'It is not filed under any kind of class, and its programme is not shown on the ' +
            'website. File it under a kind that is switched on.'
          )

        case 'NotFullyScheduled':
          // D-84. A class with no day, time or room meets on no dates, so there
          // is nothing to publish even when everything else agrees.
          return 'It needs a day, a start and end time, and a room before it can be listed.'
      }
    })
}

/**
 * An age box as a number, or null for no limit (D-206).
 *
 * <b>An empty box is "any", not zero.</b> A tumbling class genuinely runs for
 * all ages, and reading a cleared field as `0` would write "ages 0 and up" —
 * which looks deliberate on the public page and is not what anybody typed.
 */
export function years(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed.length === 0) return null

  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null
}

/**
 * What is wrong with a pair of age boxes, if anything.
 *
 * Mirrors the server so the answer arrives while she is typing rather than
 * after a save — the range is the one field here where a plausible-looking
 * mistake changes who the public page offers a class to.
 */
export function agesProblem(min: string, max: string): string | null {
  const from = years(min)
  const to = years(max)

  for (const value of [from, to]) {
    if (value !== null && (value < 0 || value > 99)) {
      return 'Ages are in years, somewhere between 0 and 99.'
    }
  }

  if (from !== null && to !== null && to < from) {
    return 'The oldest age cannot be below the youngest.'
  }

  return null
}

/**
 * How much of the season's music is on file.
 *
 * The number that turns "someone should add the music links" into a task with
 * an end — it is the same reason the registers screen counts what is missing.
 */
export function musicProgress(rows: ClassRow[]): { withMusic: number; total: number } {
  return { withMusic: rows.filter((r) => r.hasMusic).length, total: rows.length }
}

// ---- Searching, filtering and grouping, all in the browser (D-205) ----

/**
 * The cuts the list can be read in (D-205, amended D-223).
 *
 * <b>Programme was a fourth and is gone.</b> It duplicated the Rec/Comp filter
 * beside it, and with only two programmes it produced at most two headings —
 * one, in practice, since a filter is usually on. A cut that answers a question
 * the filter already answers is a control to explain rather than use.
 */
export type GroupBy = 'kind' | 'day' | 'flat'
export type Visibility = 'all' | 'public' | 'hidden'
export type Programme = 'all' | 'rec' | 'comp'

export type ClassFilter = {
  search: string
  visibility: Visibility
  programme: Programme
  /**
   * Show only the alternating classes nobody has confirmed a week for (D-603).
   *
   * Keyed on the data rather than on searching the name for `BIWKLY`: the word
   * is how the import guessed, and a class the studio has since answered still
   * carries it.
   */
  unconfirmedWeekOnly: boolean

  /**
   * One teacher's classes, picked rather than typed (D-762).
   *
   * <b>Matched on the name the rows already carry.</b> `instructors` comes off
   * the class record, so the teachers offered are the set actually teaching
   * something rather than the whole staff table: every name in the list
   * selects at least one class, and somebody who teaches nothing is not
   * offered a filter that would empty the screen.
   */
  teacher: string
}

export const NO_FILTER: ClassFilter = {
  search: '',
  visibility: 'all',
  programme: 'all',
  teacher: '',
  unconfirmedWeekOnly: false,
}

/**
 * Whether a class survives the filter bar.
 *
 * <b>Search covers the routine name and the room as well as the class name.</b>
 * A Jackrabbit name is machine-shaped — `COMP_TECH_BALLET_1PRETEAM` — so the
 * word somebody actually remembers about a class is often the one the studio
 * gave it, not the one the export did.
 */
export function matches(row: ClassRow, filter: ClassFilter): boolean {
  if (filter.unconfirmedWeekOnly && !row.meetingWeekUnconfirmed) return false

  if (filter.visibility === 'public' && !row.showInPublicCatalog) return false
  if (filter.visibility === 'hidden' && row.showInPublicCatalog) return false

  if (filter.programme === 'rec' && row.isCompetitive) return false
  if (filter.programme === 'comp' && !row.isCompetitive) return false

  /*
   * <b>Picked, and therefore exact</b> (D-762). `asked`: *"add a filter on
   * this page to search by teacher, so we don't have to type there names in."*
   * The value comes from a list built out of these same rows, so an exact
   * match is the right one - a contains would make *Ashley* select both
   * Ashleys, which is D-82's two-Ashleys problem arriving through a control
   * rather than through data.
   */
  if (filter.teacher && !instructorsOf(row).includes(filter.teacher)) return false

  const term = filter.search.trim().toLowerCase()
  if (term.length === 0) return true

  /*
   * <b>Typing a teacher's name works too</b> (D-762). It did not: the box
   * offered *class, routine, room or day* and a teacher's name only ever
   * matched by accident, because a Jackrabbit name often ends in one
   * (`… | TU 500 | 2-5 | BAILEY JO`). That accident is why the owner had been
   * typing names in at all, and it fails for exactly the classes the studio
   * renamed itself.
   */
  return [row.name, row.routineName, row.roomName, row.programName, row.dayOfWeek, ...instructorsOf(row)]
    .some((field) => field?.toLowerCase().includes(term))
}

/**
 * The teachers to offer, in the order a person reads names (D-762).
 *
 * <b>Derived from the rows, never fetched.</b> The screen already holds every
 * class and each one carries its teachers, so a second request would be a
 * second answer to a question the page can already answer - and one that could
 * disagree with the list it is filtering.
 */
export function teachersIn(rows: readonly ClassRow[]): string[] {
  return [...new Set(rows.flatMap(instructorsOf))]
    .filter((name) => name.trim().length > 0)
    .sort((a, b) => a.localeCompare(b))
}

/**
 * The teachers on a row, tolerating a row that has none (D-762).
 *
 * <b>Not defensiveness for its own sake — a deploy makes this reachable.</b>
 * The app answers 502 for about 75 seconds while it restarts (D-33), and
 * either side of that a browser can hold this bundle while the API is still
 * the previous one. An older API sends no `instructors`, and a bare
 * `row.instructors.flatMap` then throws inside render and takes the whole
 * classes screen down — a white page, on the screen the studio uses most,
 * during the minutes somebody is most likely to be watching a deploy.
 *
 * Found by a test whose fixture predates the field, which is the same thing
 * one layer down.
 */
function instructorsOf(row: ClassRow): string[] {
  return Array.isArray(row.instructors) ? row.instructors : []
}

export type ClassGroup = {
  /** The kind id, the day number, the programme name — or null for the catch-all. */
  key: string | null
  label: string
  rows: ClassRow[]
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * The list, cut the way the person is currently working.
 *
 * <b>One list, four cuts</b>, rather than four screens: Kind for the website
 * work, Day for "what runs on Tuesday", Programme for rec against comp, and
 * Flat for when the search box is doing the work. No screen is then the wrong
 * shape for the job in hand.
 *
 * <b>Grouping by kind lists every kind, including the empty ones.</b> A kind
 * created a moment ago has no classes yet, and building the list from the
 * classes alone would make it disappear the instant it was added — leaving
 * nothing to file anything into. Emptiness is also the state worth seeing: a
 * kind with no classes renders nothing on the public page.
 *
 * <b>The catch-all group always sorts last and never disappears.</b> A class
 * filed under no kind is still listed on the public page, just without a
 * heading — so it is the one thing on the screen somebody has to act on, and
 * hiding it would leave "notice it missing from the site" as the only way to
 * find it.
 */
export function group(
  rows: ClassRow[],
  by: GroupBy,
  /** Ids in the order Tonya dragged them into, mapped to their names. */
  kindNames: Map<string, string>,
  /**
   * Whether the kinds actually loaded (D-209).
   *
   * <b>A different question from "is the map empty".</b> A studio with no kinds
   * yet and a studio whose kinds failed to load produce the same empty map, and
   * only one of them can honestly be described. Without this, a failed request
   * labelled every heading "A kind that no longer exists" — a confident,
   * alarming claim about data that was completely fine.
   */
  kindsLoaded = true,
  /**
   * Whether a filter is narrowing the list (D-215).
   *
   * Empty kinds are worth a heading when showing everything and are noise the
   * moment somebody has asked a narrower question.
   */
  narrowed = false,
): ClassGroup[] {
  const ordered = [...rows].sort(byDayThenTime)

  // Nothing to group *by* yet. One honest list beats a page of headings each
  // announcing that a kind was deleted.
  if (by === 'flat' || (by === 'kind' && !kindsLoaded)) {
    return [{ key: null, label: '', rows: ordered }]
  }

  const buckets = new Map<string | null, ClassRow[]>()

  /*
   * Every kind gets a heading, so one with nothing in it can still be renamed,
   * reordered and filed into — otherwise a kind vanishes the instant it is
   * created (D-205).
   *
   * <b>Only while showing everything, though (D-215).</b> Under "On the site" a
   * kind with no public classes does not appear on the site either, so listing
   * it is a row about nothing: the studio's six competitive kinds sat at the top
   * of the public-only view saying "0 classes", which is the opposite of what
   * that filter was asked for.
   */
  if (by === 'kind' && !narrowed) for (const id of kindNames.keys()) buckets.set(id, [])

  for (const row of ordered) {
    const key = by === 'kind' ? row.classTypeId : String(row.dayNumber)

    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key)!.push(row)
  }

  const order = [...kindNames.keys()]

  const label = (key: string | null): string => {
    if (key === null) return 'Not in a kind'
    if (by === 'kind') return kindNames.get(key) ?? 'A kind that no longer exists'
    return DAYS[Number(key)] ?? key
  }

  return [...buckets.entries()]
    .map(([key, group]) => ({ key, label: label(key), rows: group }))
    // The unfiled group carries nothing when grouping any other way, and an
    // empty heading is a heading that reads as broken.
    .filter((g) => (by === 'kind' && !narrowed) || g.rows.length > 0)
    .sort((a, b) => {
      // The catch-all last, whatever it is called.
      if (a.key === null) return 1
      if (b.key === null) return -1
      if (by === 'kind') return kindOrder(a.key, order) - kindOrder(b.key, order)
      return Number(a.key) - Number(b.key)
    })
}

/** Kinds follow the order Tonya dragged them into; the map is already in it. */
function kindOrder(key: string, order: string[]): number {
  const index = order.indexOf(key)
  return index < 0 ? Number.MAX_SAFE_INTEGER : index
}

/**
 * Within a group, by when it meets.
 *
 * A class with no start time sorts last rather than first — 86 solos are
 * awaiting a slot (D-84), and letting them head every list would bury the
 * classes that actually run.
 */
function byDayThenTime(a: ClassRow, b: ClassRow): number {
  if (a.dayNumber !== b.dayNumber) return a.dayNumber - b.dayNumber

  const at = a.startTime ?? '99:99'
  const bt = b.startTime ?? '99:99'
  if (at !== bt) return at.localeCompare(bt)

  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
}

/**
 * What to show for a class inside a heading that already says the rest.
 *
 * Under `TIPPY TOES`, repeating the prefix on all seven rows is noise — so the
 * row leads with what distinguishes it. <b>Display only</b>: the stored name is
 * untouched, because D-88 keys class identity on it.
 *
 * Falls back to the whole name whenever trimming would leave too little to
 * recognise, which is the case that matters — a shortened row nobody can
 * identify is worse than a repetitive one.
 */
export function shortName(name: string, heading: string): string {
  const trimmedHeading = heading.trim()
  if (trimmedHeading.length === 0) return name

  const lower = name.toLowerCase()
  if (!lower.startsWith(trimmedHeading.toLowerCase())) return name

  const rest = name.slice(trimmedHeading.length).replace(/^[\s|\-–—:]+/, '').trim()
  return rest.length >= 3 ? rest : name
}

/**
 * What is unfinished, counted rather than left to be noticed.
 *
 * Every one of these is invisible until somebody opens the public site and
 * scrolls, which is not a check anybody performs on a Tuesday evening.
 */
export function needsAttention(rows: ClassRow[]): {
  unfiled: number
  unscheduled: number
  overfull: number
  unconfirmedWeek: number
} {
  const publicRec = rows.filter((r) => r.showInPublicCatalog && !r.isCompetitive)

  return {
    unfiled: publicRec.filter((r) => r.classTypeId === null).length,
    unscheduled: publicRec.filter((r) => !r.isFullyScheduled).length,
    overfull: rows.filter((r) => r.maxSize !== null && r.enrolled > r.maxSize).length,
    /*
     * Every class, not only the public ones (D-603). All twelve are solos,
     * duets and trios — competitive, and never on the website — so counting
     * this the way the three above are counted would report none of them.
     */
    unconfirmedWeek: rows.filter((r) => r.meetingWeekUnconfirmed).length,
  }
}

/**
 * What stopped a class being removed, when something did (D-413).
 *
 * A refusal is data rather than an exception, the same shape `createBooking`
 * takes: the panel has to show her the number and let her decide, and a bare
 * "could not" sends her looking for a fault that is not there.
 */
export type ArchiveRefusal = {
  title: string
  detail: string
  /** Set when dancers are still in it — she may go ahead. */
  enrolled?: number
  /** Set when registers have been marked — she may not. */
  attendance?: number
  /**
   * Set when the class came from Jackrabbit — she may go ahead (D-859).
   *
   * **A warning rather than the refusal it used to be.** A class Jackrabbit has
   * already deleted could be removed from neither side: the import never archives
   * on absence, and this screen refused it. It only comes back if Jackrabbit still
   * has it, which she knows and the app does not.
   */
  imported?: boolean
}

/**
 * Archives a class the studio made, or explains what is in the way (D-413).
 *
 * `asked`: *"we need a way to delete a class if she no longer want it."*
 * Archived rather than deleted (A-12), and its sessions go with it — they are
 * drawn from the class (A-1), so leaving them puts dated rows on the calendar
 * belonging to a class that no longer exists.
 */
export async function archiveClass(
  classId: string,
  confirm = false,
): Promise<{ ok: true } | { ok: false; refusal: ArchiveRefusal }> {
  const res = await fetch(
    `/api/admin/classes/${classId}${confirm ? '?confirm=true' : ''}`,
    { method: 'DELETE' },
  )

  if (res.ok) return { ok: true }

  const problem = await res.json().catch(() => null)

  return {
    ok: false,
    refusal: {
      title: problem?.title ?? 'Could not remove that class',
      detail: problem?.detail ?? `Something went wrong (${res.status}).`,
      enrolled: problem?.enrolled,
      attendance: problem?.attendance,

      /*
       * ⚠ Without this the D-859 warning is a refusal wearing new words: `mayConfirm`
       * reads `refusal.imported`, so an absent field leaves the second button hidden and
       * the box in the crit colours — and the branch it gates is the one the whole change
       * exists for, a deleted placeholder with nobody in it. A typecheck cannot see a
       * missing optional property, and no test constructed one.
       */
      imported: problem?.imported,
    },
  }
}

/**
 * A class whose name claims technique while its filing says otherwise (D-506).
 *
 * `needsStyle` and `needsLevel` are carried separately because an empty style
 * or level is an outright refusal in the matcher — filing the category and
 * stopping there leaves the class exactly as unusable, which is the trap
 * somebody fixing these by eye falls into.
 */
export type FilingConflict = {
  classId: string
  name: string
  category: 'Tech' | 'Choreo' | 'Recital' | null
  style: string | null
  levelBand: string
  /** The name claims technique and the filing denies it - the loud half. */
  nameDisagrees: boolean
  needsStyle: boolean
  needsLevel: boolean
  /** Somebody has already claimed one of the three, so imports leave it alone. */
  touchedByHand: boolean
}

/**
 * The cache key for the mis-filed list, in one place (D-506).
 *
 * <b>Two screens answer "is this class still filed against its name".</b> The
 * banner asks it, and the class panel changes the answer — so the panel has to
 * refresh this, and a key spelled out at both call sites is the drift that
 * makes a banner go on stating something the person just fixed.
 */
export const FILING_CONFLICTS_KEY = ['class-filing-conflicts'] as const

export async function fetchFilingConflicts(): Promise<FilingConflict[]> {
  const res = await fetch('/api/admin/classes/filing-conflicts')
  if (!res.ok) throw new Error(`Could not load that (${res.status})`)
  return res.json()
}

/**
 * Files a class's makeup category, style and level, and nothing else (D-506).
 *
 * A narrow route rather than the panel's save: that one writes the routine
 * name, music and description unconditionally, so a fixer built on it could
 * blank a routine name it never meant to touch (D-280).
 */
export async function setClassFiling(
  id: string,
  filing: { category: string | null; style: string | null; levelBand: string | null },
): Promise<void> {
  const res = await fetch(`/api/admin/classes/${id}/filing`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filing),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? `Could not file that (${res.status})`)
  }
}

/**
 * Sets who teaches a class — the whole set, first one primary (D-537).
 *
 * **There was no way to do this at all.** The import has built classes with two
 * teachers since the beginning and the app could set one at creation, never
 * afterwards — and the import does not update them either, so a class naming
 * the wrong teacher could not be corrected anywhere.
 */
export async function setClassTeachers(
  classId: string,
  staffIds: string[],
  studentTeacherIds: string[] = [],
): Promise<void> {
  const res = await fetch(`/api/admin/classes/${classId}/teachers`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffIds, studentTeacherIds }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Could not save that (${res.status})`)
  }
}

/**
 * Puts one night of a class, or the rest of them, in a different room (D-569).
 *
 * Written as an override on the dated session rather than on the class (A-1):
 * a class's room comes from Jackrabbit and every import rewrites it, so the
 * occurrence is the one place the studio owns. Pass `roomId: null` to put it
 * back where the class says.
 *
 * The server's refusal is the message shown, because it is the one that knows
 * which night was in the way.
 */
export async function moveOccurrenceRoom(
  classId: string,
  occurrenceId: string,
  move: { roomId: string | null; rest: boolean },
): Promise<void> {
  const res = await fetch(`/api/admin/classes/${classId}/occurrences/${occurrenceId}/room`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(move),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? `Could not move that (${res.status})`)
  }
}
